import { CONTACT } from "@/lib/data";
import SEO from "@/components/SEO";

const Section = ({ title, children, testId }) => (
  <section className="mb-12" data-testid={testId}>
    <h2 className="font-serif text-2xl md:text-3xl text-ink leading-tight mb-5">
      {title}
    </h2>
    <div className="font-sans text-sm md:text-base text-ink/75 leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const Mentions = () => {
  return (
    <div className="bg-sand min-h-screen pb-32" data-testid="page-mentions">
      <SEO
        title="Mentions Légales & RGPD"
        description="Mentions légales, conformité RGPD, cookies et médiation de la consommation de Terre et Mer Conciergerie (SAS Ma Conciergerie Bretonne)."
        noindex={false}
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Mentions Légales", path: "/mentions-legales" },
        ]}
      />
      {/* Header */}
      <div className="bg-white border-b border-ink/10">
        <div className="container-tm py-16 lg:py-20">
          <span className="overline">Informations légales</span>
          <h1 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-3xl">
            Mentions
            <span className="italic text-gold"> légales</span>.
          </h1>
          <p className="mt-5 font-sans text-base text-ink/65 leading-relaxed max-w-2xl">
            Conformément aux dispositions des articles 6-III et 19 de la Loi n°2004-575 du 21 juin 2004 pour la Confiance dans l'économie numérique (LCEN).
          </p>
        </div>
      </div>

      <div className="container-tm py-16 max-w-4xl">
        <Section title="Éditeur du site" testId="section-editeur">
          <p>
            Le site <strong className="text-ink">terreetmer-conciergerie.fr</strong> est conçu et édité par la <strong className="text-ink">SAS MA CONCIERGERIE BRETONNE</strong>, société par actions simplifiée au capital de 6 000 €.
          </p>
          <ul className="mt-3 space-y-1.5">
            <li><span className="text-ink/55">Siège social :</span> <strong className="text-ink">40 Bis Rue de Dinard, 35730 PLEURTUIT</strong></li>
            <li><span className="text-ink/55">RCS :</span> Saint-Malo</li>
            <li><span className="text-ink/55">SIRET :</span> 883 753 469 000 21</li>
            <li><span className="text-ink/55">N° TVA intracommunautaire :</span> FR62883753469</li>
            <li><span className="text-ink/55">Directeur de la publication :</span> SAS MA CONCIERGERIE BRETONNE</li>
            <li>
              <span className="text-ink/55">Contact :</span>{" "}
              <a href={`mailto:${CONTACT.email}`} className="text-gold hover:underline">
                {CONTACT.email}
              </a>
            </li>
            <li>
              <span className="text-ink/55">Téléphone :</span>{" "}
              <a href={CONTACT.phoneHref} className="text-gold hover:underline">
                {CONTACT.phone}
              </a>
            </li>
          </ul>
        </Section>

        <Section title="Hébergement" testId="section-hebergement">
          <p>
            Le site <strong className="text-ink">terreetmer-conciergerie.fr</strong> est hébergé par <strong className="text-ink">1&1 IONOS</strong>, SARL au capital de 100 000 €.
          </p>
          <ul className="mt-3 space-y-1.5">
            <li><span className="text-ink/55">Siège social :</span> 7 place de la Gare, BP 70109, 57201 Sarreguemines Cedex</li>
            <li><span className="text-ink/55">RCS :</span> Sarreguemines B 431 303 775</li>
            <li><span className="text-ink/55">SIRET :</span> 431 303 775 000 16</li>
          </ul>
        </Section>

        <Section title="Propriété intellectuelle" testId="section-propriete">
          <p>
            <strong className="text-ink">TERRE ET MER CONCIERGERIE</strong> est une marque déposée.
          </p>
          <p>
            Copyright © {new Date().getFullYear()}, La reproduction, même partielle, de tout texte, image ou photo présents sur ce site est strictement interdite sans accord préalable et écrit de la SAS MA CONCIERGERIE BRETONNE.
          </p>
          <p>
            Les logos, marques et marques déposées (Airbnb, Booking.com, Vrbo, Tripadvisor…) cités sur ce site demeurent la propriété de leurs détenteurs respectifs et sont mentionnés à titre purement informatif. TERRE ET MER CONCIERGERIE n'est pas affiliée à ces plateformes.
          </p>
        </Section>

        <Section title="Données personnelles & RGPD" testId="section-rgpd">
          <p>
            Conformément au Règlement Général sur la Protection des Données (RGPD, Règlement UE 2016/679) et à la Loi Informatique et Libertés modifiée, les données personnelles collectées via le formulaire de contact (nom, prénom, téléphone, email, ville et type de logement) font l'objet d'un traitement automatisé par la SAS MA CONCIERGERIE BRETONNE.
          </p>
          <p>
            <strong className="text-ink">Finalité du traitement :</strong> répondre à votre demande de contact, vous proposer une étude personnalisée de votre logement et, le cas échéant, vous adresser nos communications commerciales.
          </p>
          <p>
            <strong className="text-ink">Durée de conservation :</strong> 3 ans à compter du dernier contact pour les prospects ; durée de la relation contractuelle augmentée de 5 ans pour les clients.
          </p>
          <p>
            <strong className="text-ink">Vos droits :</strong> conformément aux articles 15 à 22 du RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité, de limitation et d'opposition concernant vos données. Pour exercer ces droits, adressez votre demande par email à{" "}
            <a href={`mailto:${CONTACT.email}`} className="text-gold hover:underline">{CONTACT.email}</a> en justifiant de votre identité.
          </p>
          <p>
            Vous pouvez également introduire une réclamation auprès de la <strong className="text-ink">Commission Nationale de l'Informatique et des Libertés (CNIL)</strong>, 3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07, <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">www.cnil.fr</a>.
          </p>
          <p className="text-xs text-ink/55 italic">
            Vos données ne sont jamais cédées, vendues ou louées à des tiers sans votre consentement explicite.
          </p>
        </Section>

        <Section title="Démarchage téléphonique (BLOCTEL)" testId="section-bloctel">
          <p>
            Conformément à la loi du 17 mars 2014 relative à la consommation, vous pouvez à tout moment vous inscrire gratuitement sur la liste d'opposition au démarchage téléphonique{" "}
            <a href="https://www.bloctel.gouv.fr" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
              BLOCTEL
            </a>{" "}
            (www.bloctel.gouv.fr).
          </p>
        </Section>

        <Section title="Cookies & traceurs" testId="section-cookies">
          <p>
            Ce site utilise uniquement des cookies techniques nécessaires à son bon fonctionnement (session, préférences d'affichage). Aucun cookie de profilage publicitaire ou de mesure d'audience tierce n'est déposé sans votre consentement explicite.
          </p>
          <p>
            Vous pouvez à tout moment configurer votre navigateur pour refuser les cookies ou en supprimer l'enregistrement. Le refus des cookies techniques peut altérer le bon fonctionnement de certaines pages.
          </p>
        </Section>

        <Section title="Liens hypertextes" testId="section-liens">
          <p>
            Le site <strong className="text-ink">terreetmer-conciergerie.fr</strong> peut contenir des liens vers des sites tiers (plateformes de réservation, partenaires, médias). La SAS MA CONCIERGERIE BRETONNE n'exerce aucun contrôle sur le contenu de ces sites externes et ne saurait être tenue responsable de leur contenu, de leurs pratiques ou des dommages éventuels résultant de leur consultation.
          </p>
          <p>
            La création de liens vers le site terreetmer-conciergerie.fr est autorisée à condition qu'ils ne portent pas atteinte aux intérêts de la SAS MA CONCIERGERIE BRETONNE et qu'ils mentionnent clairement la source.
          </p>
        </Section>

        <Section title="Limitation de responsabilité" testId="section-responsabilite">
          <p>
            Les informations diffusées sur ce site sont fournies à titre indicatif et peuvent être modifiées à tout moment sans préavis. Les simulations de revenus locatifs présentées ne constituent pas un engagement contractuel et ne tiennent pas lieu de garantie de résultat.
          </p>
          <p>
            La SAS MA CONCIERGERIE BRETONNE met tout en œuvre pour assurer l'exactitude des informations diffusées sur ce site mais ne saurait être tenue responsable des erreurs, omissions ou résultats qui pourraient découler de leur utilisation.
          </p>
        </Section>

        <Section title="Litiges & médiation de la consommation" testId="section-mediation">
          <p>
            En cas de litige entre le Client et l'entreprise, ceux-ci s'efforceront de le résoudre à l'amiable (le Client adressera une réclamation écrite auprès du professionnel ou, le cas échéant, auprès du Service Relations Clientèle du professionnel).
          </p>
          <p>
            À défaut d'accord amiable ou en l'absence de réponse du professionnel dans un délai raisonnable d'un (1) mois, le Client consommateur au sens de l'article L.133-4 du code de la consommation a la possibilité de saisir gratuitement, si un désaccord subsiste, le médiateur compétent inscrit sur la liste des médiateurs établie par la Commission d'évaluation et de contrôle de la médiation de la consommation en application de l'article L.615-1 du code de la consommation, à savoir :
          </p>
          <div className="mt-4 p-5 rounded-2xl bg-cream/60 border border-gold/30">
            <p className="font-serif text-lg text-ink mb-1">La Société Médiation Professionnelle</p>
            <p className="text-sm">
              <a href="https://www.mediateur-consommation-smp.fr" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
                www.mediateur-consommation-smp.fr
              </a>
            </p>
            <p className="text-sm mt-1">24 rue Albert de Mun, 33000 Bordeaux</p>
          </div>
          <p className="mt-4">
            Conformément à l'article 14 du Règlement (UE) n° 524/2013, la Commission Européenne a mis en place une plateforme de Règlement en Ligne des Litiges (RLL) accessible à l'adresse :{" "}
            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline break-all">
              ec.europa.eu/consumers/odr
            </a>.
          </p>
        </Section>

        <Section title="Droit applicable & juridiction compétente" testId="section-juridiction">
          <p>
            Les présentes mentions légales et l'utilisation du site sont régies par le droit français. En cas de litige et après échec de toute tentative de résolution amiable, les tribunaux français seront seuls compétents pour en connaître.
          </p>
        </Section>

        <p className="mt-16 text-xs text-ink/50 italic font-sans">
          Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long" })}.
        </p>
      </div>
    </div>
  );
};

export default Mentions;
